import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, BRAND } from "@/lib/api";
import PrintHeader from "@/components/PrintHeader";
import LetterheadToggle, { useLetterheadMode } from "@/components/LetterheadToggle";

/** Tiny section-block helper for the A4-portrait Rx. */
function Block({ title, children, hidden = false, note = "" }) {
  if (hidden) return null;
  return (
    <div className="mb-2.5 break-inside-avoid">
      <div className="mb-1 flex items-baseline justify-between gap-2 border-b border-brand/40">
        <span className="text-[8.5px] font-bold uppercase tracking-widest text-brand">{title}</span>
        {note && <span className="text-[8px] font-medium normal-case tracking-normal text-ink-muted">{note}</span>}
      </div>
      <div className="text-[10.5px] leading-snug">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) return null;
  return (
    <div><span className="text-ink-muted">{k}:</span> <span className="font-medium">{Array.isArray(v) ? v.map((x) => x.name || x).join(", ") : v}</span></div>
  );
}

/** Eye reading data row — OD (Right Eye) on LEFT column, OS (Left Eye) on RIGHT column. */
function EyeRow({ od, os, label }) {
  if (!od && !os) return null;
  return (
    <div className="grid grid-cols-[80px_1fr_1fr] gap-1 border-b border-border/60 py-0.5">
      <span className="text-ink-muted">{label}</span>
      <span data-testid={`eye-od-${label}`}>{od || "—"}</span>
      <span data-testid={`eye-os-${label}`}>{os || "—"}</span>
    </div>
  );
}

/** Column header row placed ABOVE eye data — "Right Eye" (OD) left, "Left Eye" (OS) right. */
function EyeHeader() {
  return (
    <div className="grid grid-cols-[80px_1fr_1fr] gap-1 border-b border-brand/40 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-brand">
      <span />
      <span>Right Eye (OD)</span>
      <span>Left Eye (OS)</span>
    </div>
  );
}

export default function PrintConsultation() {
  const { visitId } = useParams();
  const [c, setC] = useState(null);
  const [patient, setPatient] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [branch, setBranch] = useState(null);
  const letterhead = useLetterheadMode();
  const printedRef = useRef(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/opd/consultations/by-visit/${visitId}`);
      // Pull the optometry record for this visit (if any) and use it as a
      // fallback for VA / refraction / basic exam fields whenever the
      // consultation itself doesn't carry them. Optometrists fill these in
      // during pre-exam and doctors expect them to appear on the printed Rx
      // even if they didn't manually copy the values over.
      const opt = await api.get(`/opd/optometry/by-visit/${visitId}`).then(r => r.data || {}).catch(() => ({}));
      const merged = { ...data };
      // Visual acuity — distant / near from optometry only when consultation is blank.
      if (!merged.va_unaided?.od && !merged.va_unaided?.os && (opt.distant_vision_od || opt.distant_vision_os)) {
        merged.va_unaided = { od: opt.distant_vision_od || "", os: opt.distant_vision_os || "" };
      }
      if (!merged.va_near?.od && !merged.va_near?.os && (opt.near_vision_od || opt.near_vision_os)) {
        merged.va_near = { od: opt.near_vision_od || "", os: opt.near_vision_os || "" };
      }
      // Refraction — subjective is the doctor-friendly one; fall back to auto.
      const optOd = (opt.subjective_od && (opt.subjective_od.sph || opt.subjective_od.cyl || opt.subjective_od.axis || opt.subjective_od.add))
        ? opt.subjective_od : opt.auto_refraction_od;
      const optOs = (opt.subjective_os && (opt.subjective_os.sph || opt.subjective_os.cyl || opt.subjective_os.axis || opt.subjective_os.add))
        ? opt.subjective_os : opt.auto_refraction_os;
      const hasR = (e) => e && (e.sph || e.cyl || e.axis || e.add);
      if (!hasR(merged.refraction_od) && hasR(optOd)) merged.refraction_od = optOd;
      if (!hasR(merged.refraction_os) && hasR(optOs)) merged.refraction_os = optOs;
      // Examination bits the optometry captures — color vision only right now.
      if (!merged.color_vision && opt.color_vision) merged.color_vision = opt.color_vision;
      setC(merged);
      if (data && data.patient_id) {
        const p = await api.get(`/patients/${data.patient_id}`);
        setPatient(p.data.patient);
      }
      const me = await api.get("/auth/me");
      setHospital(me.data.hospital);
      setBranch(me.data.branch);
      // Auto-print only when in "with header" mode (default). In letterhead mode user explicitly clicks Print.
      const auto = new URLSearchParams(window.location.search).get("autoprint");
      if (!printedRef.current && auto !== "0") {
        printedRef.current = true;
        setTimeout(() => window.print(), 700);
      }
    })();
  }, [visitId]);

  if (!c || !patient) return <div className="p-10 text-sm text-ink-muted">Loading…</div>;

  const hasRefraction = (eye) => (eye?.sph || eye?.cyl || eye?.axis || eye?.add);
  const hasAnyHistory = c.chief_complaints || c.duration || c.history_present_illness || c.past_ocular_history
    || c.past_medical_history || c.surgical_history || c.allergy || c.current_medication || c.family_history
    || (c.systemic_illness && c.systemic_illness.length);
  const hasPain = (c.pain_assessment?.score || 0) > 0 || c.pain_assessment?.site || c.pain_assessment?.character;
  const hasNutrition = c.nutrition_assessment?.bmi || c.nutrition_assessment?.appetite || c.nutrition_assessment?.must_score;
  const hasVA = c.va_unaided?.od || c.va_unaided?.os || c.va_bcva?.od || c.va_bcva?.os || c.va_pinhole?.od || c.va_pinhole?.os || c.va_near?.od || c.va_near?.os;
  const hasExam = c.iop?.od || c.iop?.os || c.slit_lamp?.od || c.slit_lamp?.os || c.fundus?.od || c.fundus?.os
    || c.lacrimal || c.color_vision || c.ocular_motility || c.pupils;

  return (
    <div className="bg-white text-ink">
      {/* Print-only CSS — A4 portrait, narrow margins, exact colour preservation */}
      <style>{`
        @page { size: A4 portrait; margin: 10mm 11mm; }
        @media print {
          html, body { background: #fff !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .page-break-before { page-break-before: always; }
        }
        .rx-table thead th { background: #f5f5f5 !important; }
      `}</style>

      <div className="mx-auto max-w-[190mm] p-3 text-[10.5px]">
        <LetterheadToggle />
        {/* Header (hidden in letterhead mode) */}
        {!letterhead && (
          <PrintHeader
            compact
            hospital={hospital}
            branch={branch}
            subtitle="OPD Consultation Rx"
            right={<>
              <div>Date: <b>{(c.created_at || "").slice(0, 10)}</b></div>
              <div>Doctor: <b>{c.doctor_name}</b></div>
              {c.doctor_registration_no && <div>Reg: {c.doctor_registration_no}</div>}
            </>}
          />
        )}

        {/* Patient strip */}
        <div className="mt-2 grid grid-cols-5 gap-2 rounded border border-border bg-secondary/40 p-1.5 text-[10px]">
          <div><div className="text-[8px] uppercase text-ink-muted">Patient</div><div className="font-semibold">{patient.name}</div></div>
          <div><div className="text-[8px] uppercase text-ink-muted">UHID</div><div className="font-mono">{patient.uhid}</div></div>
          <div><div className="text-[8px] uppercase text-ink-muted">Age / Sex</div><div>{patient.age || "—"}{patient.gender ? ` · ${patient.gender}` : ""}</div></div>
          <div><div className="text-[8px] uppercase text-ink-muted">Mobile</div><div>{patient.mobile || "—"}</div></div>
          <div><div className="text-[8px] uppercase text-ink-muted">{patient.abha_number ? "ABHA" : "ID"}</div><div className="truncate font-mono text-[9px]">{patient.abha_number || patient.id_proof_value || "—"}</div></div>
        </div>

        {/* Two-column layout for compactness */}
        <div className="mt-3 grid grid-cols-2 gap-3">
          {/* LEFT column */}
          <div>
            <Block title="History" hidden={!hasAnyHistory}>
              <KV k="Chief complaints" v={c.chief_complaints} />
              <KV k="Duration" v={c.duration} />
              <KV k="HPI" v={c.history_present_illness} />
              <KV k="Past ocular" v={c.past_ocular_history} />
              <KV k="Past medical" v={c.past_medical_history} />
              <KV k="Surgical" v={c.surgical_history} />
              <KV k="Allergy" v={c.allergy} />
              <KV k="Current meds" v={c.current_medication} />
              <KV k="Family hx" v={c.family_history} />
              <KV k="Systemic" v={c.systemic_illness} />
            </Block>

            <Block title="Pain assessment" hidden={!hasPain}>
              <KV k="VAS score" v={`${c.pain_assessment?.score || 0}/10`} />
              <KV k="Site" v={c.pain_assessment?.site} />
              <KV k="Character" v={c.pain_assessment?.character} />
              <KV k="Frequency" v={c.pain_assessment?.frequency} />
              <KV k="Onset" v={c.pain_assessment?.onset} />
              <KV k="Aggravating" v={c.pain_assessment?.aggravating} />
              <KV k="Relieving" v={c.pain_assessment?.relieving} />
            </Block>

            <Block title="Nutrition assessment (MUST)" hidden={!hasNutrition}>
              <KV k="Weight" v={c.nutrition_assessment?.weight_kg && `${c.nutrition_assessment.weight_kg} kg`} />
              <KV k="Height" v={c.nutrition_assessment?.height_cm && `${c.nutrition_assessment.height_cm} cm`} />
              <KV k="BMI" v={c.nutrition_assessment?.bmi} />
              <KV k="Weight loss" v={c.nutrition_assessment?.weight_loss_pct && `${c.nutrition_assessment.weight_loss_pct}%`} />
              <KV k="Appetite" v={c.nutrition_assessment?.appetite} />
              <KV k="Feeding route" v={c.nutrition_assessment?.feeding_route} />
              <KV k="Diet" v={c.nutrition_assessment?.diet} />
              <KV k="Allergies" v={c.nutrition_assessment?.food_allergies} />
              <KV k="MUST risk" v={c.nutrition_assessment?.must_score} />
            </Block>

            <Block title="Visual acuity" hidden={!hasVA}>
              <EyeHeader />
              <EyeRow label="Unaided" od={c.va_unaided?.od} os={c.va_unaided?.os} />
              <EyeRow label="BCVA" od={c.va_bcva?.od} os={c.va_bcva?.os} />
              <EyeRow label="Pinhole" od={c.va_pinhole?.od} os={c.va_pinhole?.os} />
              <EyeRow label="Near" od={c.va_near?.od} os={c.va_near?.os} />
            </Block>

            <Block title="Refraction" hidden={!hasRefraction(c.refraction_od) && !hasRefraction(c.refraction_os)}>
              <div className="grid grid-cols-[110px_1fr_1fr_1fr_1fr] gap-1 border-b border-brand/40 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-brand">
                <span />
                <span>Sph</span><span>Cyl</span><span>Axis</span><span>Add</span>
              </div>
              <div className="grid grid-cols-[110px_1fr_1fr_1fr_1fr] gap-1 border-b border-border/60 py-0.5">
                <span className="text-ink-muted">Right Eye (OD)</span>
                <span>{c.refraction_od?.sph || "—"}</span>
                <span>{c.refraction_od?.cyl || "—"}</span>
                <span>{c.refraction_od?.axis || "—"}</span>
                <span>{c.refraction_od?.add || "—"}</span>
              </div>
              <div className="grid grid-cols-[110px_1fr_1fr_1fr_1fr] gap-1 border-b border-border/60 py-0.5">
                <span className="text-ink-muted">Left Eye (OS)</span>
                <span>{c.refraction_os?.sph || "—"}</span>
                <span>{c.refraction_os?.cyl || "—"}</span>
                <span>{c.refraction_os?.axis || "—"}</span>
                <span>{c.refraction_os?.add || "—"}</span>
              </div>
            </Block>

            {(c.refraction_od?.va || c.refraction_os?.va) && (
              <div className="-mt-1 mb-2.5 text-[10px] text-ink-muted">VA: Right Eye {c.refraction_od?.va || "—"} · Left Eye {c.refraction_os?.va || "—"}</div>
            )}
          </div>

          {/* RIGHT column */}
          <div>
            <Block title="Examination" hidden={!hasExam}>
              <EyeHeader />
              <EyeRow label="IOP" od={c.iop?.od} os={c.iop?.os} />
              <EyeRow label="Slit lamp" od={c.slit_lamp?.od} os={c.slit_lamp?.os} />
              <EyeRow label="Fundus" od={c.fundus?.od} os={c.fundus?.os} />
              <KV k="Lacrimal" v={c.lacrimal} />
              <KV k="Color vision" v={c.color_vision} />
              <KV k="Ocular motility" v={c.ocular_motility} />
              <KV k="Pupils" v={c.pupils} />
            </Block>

            <Block title="Diagnosis & Plan" hidden={!c.diagnosis && !c.icd_code && !c.treatment_advice && !c.follow_up_date && !c.referral && !c.remarks}>
              <KV k="Diagnosis" v={c.diagnosis} />
              <KV k="ICD-10" v={c.icd_code} />
              <KV k="Treatment advice" v={c.treatment_advice} />
              <KV k="Investigations" v={c.investigations_advised} />
              <KV k="Procedures" v={c.procedure_advised} />
              <KV k="Surgery advised" v={c.surgery_advised} />
              <KV k="Follow-up" v={c.follow_up_date} />
              <KV k="Referral" v={c.referral} />
              <KV k="Remarks" v={c.remarks} />
            </Block>
          </div>
        </div>

        {/* Rx — full width below the two-column section */}
        {c.prescription && c.prescription.length > 0 && (
          <div className="mt-2 break-inside-avoid">
            <div className="mb-1 border-b border-brand/40 text-[8.5px] font-bold uppercase tracking-widest text-brand">Prescription (Rx)</div>
            <table className="rx-table w-full border border-border text-[9.5px]">
              <thead>
                <tr className="text-left">
                  <th className="border-r border-border px-1 py-1">#</th>
                  <th className="border-r border-border px-1.5 py-1">Medicine</th>
                  <th className="border-r border-border px-1.5 py-1">Strength</th>
                  <th className="border-r border-border px-1.5 py-1">Dose</th>
                  <th className="border-r border-border px-1.5 py-1">Route</th>
                  <th className="border-r border-border px-1.5 py-1">Eye</th>
                  <th className="border-r border-border px-1.5 py-1">Freq</th>
                  <th className="border-r border-border px-1.5 py-1">Timing</th>
                  <th className="border-r border-border px-1.5 py-1">Days</th>
                  <th className="px-1.5 py-1">Instructions</th>
                </tr>
              </thead>
              <tbody>
                {c.prescription.map((m, i) => (
                  <tr key={i} className="border-t border-border align-top">
                    <td className="border-r border-border px-1 py-1 text-ink-muted">{i + 1}</td>
                    <td className="border-r border-border px-1.5 py-1">
                      <div className="font-medium">{m.medicine}</div>
                      {m.generic_name && <div className="text-[8px] italic text-ink-muted">{m.generic_name}</div>}
                    </td>
                    <td className="border-r border-border px-1.5 py-1">{m.strength || "—"}</td>
                    <td className="border-r border-border px-1.5 py-1">{m.dosage || "—"}</td>
                    <td className="border-r border-border px-1.5 py-1">{m.route || "—"}</td>
                    <td className="border-r border-border px-1.5 py-1">{m.eye || "—"}</td>
                    <td className="border-r border-border px-1.5 py-1">{m.frequency || "—"}</td>
                    <td className="border-r border-border px-1.5 py-1">{m.timing || "—"}</td>
                    <td className="border-r border-border px-1.5 py-1">{m.duration || "—"}</td>
                    <td className="px-1.5 py-1">{m.instructions || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Signature & footer */}
        <div className="mt-4 flex items-end justify-between">
          <div className="text-[8.5px] text-ink-muted">Patient seen and prescribed by undersigned. NABH-compliant, time-stamped, audit-logged medico-legal document.</div>
          <div className="text-right">
            {c.doctor_signature_url && <img src={c.doctor_signature_url} alt="signature" className="ml-auto mb-1 h-10 object-contain" data-testid="print-doctor-signature" />}
            <div className="border-t border-ink pt-0.5 text-[10px] leading-tight">
              <b>{c.doctor_name}</b>
              {c.doctor_qualification && <span className="text-ink-muted"> · {c.doctor_qualification}</span>}
              <br /><span className="text-ink-muted">{c.doctor_registration_no}</span>
            </div>
          </div>
        </div>
        <div className="mt-2 border-t border-border pt-1 text-center text-[8px] text-ink-muted">{BRAND.footer}</div>

        {/* Reprint button (screen only) */}
        <div className="no-print mt-4 flex justify-end">
          <button onClick={() => window.print()} className="rounded-md bg-brand px-3 py-1.5 text-xs text-white">Print again</button>
        </div>
      </div>
    </div>
  );
}
